import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const PENDING_INVITE_TOKEN_KEY = 'pending_invite_token';

const AcceptInvitationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuth0Loading, loginWithRedirect } = useAuth0();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>(
    'pending'
  );
  const [message, setMessage] = useState<string>(
    'Accepting your invitation...'
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const pendingToken = localStorage.getItem(PENDING_INVITE_TOKEN_KEY);

    const accept = async (inviteToken: string) => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/invitations/accept?token=${encodeURIComponent(
            inviteToken
          )}`,
          {
            method: 'POST'
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => undefined);
          throw new Error(
            err?.error || `Failed to accept invitation (${res.status})`
          );
        }
        
        // Clear the pending token after successful acceptance
        localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
        
        setStatus('success');
        setMessage('Invitation accepted! Redirecting...');
        setTimeout(() => {
          // Force a full reload so auth + AppUser context re-initialize
          window.location.href = '/create';
        }, 1200);
      } catch (e: any) {
        // Clear the pending token on error too
        localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
        setStatus('error');
        setMessage(e?.message || 'Failed to accept invitation.');
      }
    };

    // Wait for Auth0 to finish loading
    if (isAuth0Loading) {
      setMessage('Loading...');
      return;
    }

    // Case 1: User just returned from signup/login and has a pending token
    if (isAuthenticated && pendingToken) {
      setMessage('Accepting your invitation...');
      accept(pendingToken);
      return;
    }

    // Case 2: User is authenticated and token is in URL (direct access while logged in)
    if (isAuthenticated && token) {
      setMessage('Accepting your invitation...');
      accept(token);
      return;
    }

    // Case 3: User is not authenticated but has token in URL (new user scenario)
    if (!isAuthenticated && token) {
      setMessage('Please sign up or log in to accept the invitation...');
      // Store the token for after authentication
      localStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
      
      // Redirect to Auth0 signup/login with appState to return to this page
      setTimeout(() => {
        loginWithRedirect({
          authorizationParams: {
            screen_hint: 'signup' // Suggest signup screen for new users
          },
          appState: {
            returnTo: '/accept-invitation'
          }
        });
      }, 1000);
      return;
    }

    // Case 3b: User is not authenticated, token already stored (likely returning from a cancelled login)
    if (!isAuthenticated && pendingToken && !token) {
      setMessage('Please sign up or log in to accept the invitation...');
      
      setTimeout(() => {
        loginWithRedirect({
          authorizationParams: {
            screen_hint: 'signup'
          },
          appState: {
            returnTo: '/accept-invitation'
          }
        });
      }, 1000);
      return;
    }

    // Case 4: No token available
    if (!token && !pendingToken) {
      setStatus('error');
      setMessage('Missing invitation token.');
      return;
    }
  }, [location.search, navigate, isAuthenticated, isAuth0Loading, loginWithRedirect]);

  const color =
    status === 'pending' ? '#555' : status === 'success' ? 'green' : 'crimson';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}
    >
      <div style={{ maxWidth: 520, textAlign: 'center' }}>
        <h2 style={{ marginBottom: 8 }}>Invitation</h2>
        <p style={{ color }}>{message}</p>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
